import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ArrowRight, ShoppingBag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import StratiqLogo from "./StratiqLogo";
import { useBasket } from "../context/BasketContext";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about" },
  { label: "SmartMatch", path: "/smartmatch" },
  { label: "Products", path: "/products" },
  { label: "Pricing", path: "/pricing" },
  { label: "Book a Call", path: "/book" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { itemCount, setIsOpen } = useBasket();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Detect if current page has a light/white background at the top
  const isLightPage = location.pathname.startsWith("/product/") || location.pathname === "/products";
  // When not scrolled on a light page, use dark text; otherwise white text on dark heroes
  const navDark = scrolled || isLightPage;

  const openAssistantFromLogo = () => {
    window.dispatchEvent(new CustomEvent("open-assistant", { detail: { fromLogo: true } }));
  };

  return (
    <>
      {/* ──── TOP CTA BANNER. Site wide ──── */}
      <div className={`fixed top-0 left-0 right-0 z-[60] text-white h-9 flex items-center justify-center transition-all duration-500 overflow-hidden ${scrolled ? "-translate-y-full" : "translate-y-0"}`} data-testid="top-cta-banner">
        <style>{`
          @keyframes bannerAmbient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes bannerFloat {
            0%, 100% { transform: translateY(0) translateX(0); opacity: 0.4; }
            25% { transform: translateY(-8px) translateX(4px); opacity: 0.7; }
            50% { transform: translateY(-3px) translateX(-3px); opacity: 0.5; }
            75% { transform: translateY(-10px) translateX(2px); opacity: 0.6; }
          }
          @keyframes bannerScroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
        {/* Slow ambient gradient base - BLACK */}
        <div className="absolute inset-0" style={{
          background: "linear-gradient(270deg, #000000, #0a0a0a, #111111, #0a0a0a, #000000)",
          backgroundSize: "400% 100%",
          animation: "bannerAmbient 20s ease infinite"
        }} />
        {/* Floating particles */}
        {[...Array(18)].map((_, i) => (
          <div key={i} className="absolute rounded-full bg-blue-400" style={{
            width: `${1.5 + Math.random() * 2}px`,
            height: `${1.5 + Math.random() * 2}px`,
            left: `${(i / 18) * 100 + Math.random() * 5}%`,
            top: `${10 + Math.random() * 70}%`,
            opacity: 0.3 + Math.random() * 0.3,
            animation: `bannerFloat ${3 + Math.random() * 4}s ease-in-out infinite`,
            animationDelay: `${Math.random() * -4}s`,
            boxShadow: "0 0 6px 1px rgba(59, 130, 246, 0.3)",
          }} />
        ))}
        {/* Shimmer pass */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(90deg, transparent 0%, transparent 30%, rgba(255,255,255,0.04) 45%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 55%, transparent 70%, transparent 100%)",
          animation: "shimmer 6s ease-in-out infinite",
        }} />
        {/* Smooth looping dual messages */}
        <div className="relative w-full overflow-hidden h-full flex items-center">
          <div className="flex whitespace-nowrap" style={{ animation: "bannerScroll 30s linear infinite" }}>
            <Link to="/smartmatch" className="inline-flex items-center gap-2 mx-12 text-xs sm:text-sm font-semibold tracking-[0.06em] uppercase hover:opacity-80 transition-opacity">
              <span className="text-white">YOUR ENTIRE <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-cyan-400 bg-clip-text text-transparent font-bold">MARKETING DEPARTMENT</span>. ONE SUBSCRIPTION. FROM £497 A MONTH</span>
              <ArrowRight size={13} className="text-blue-400" />
            </Link>
            <span className="inline-flex items-center gap-2 mx-12 text-xs sm:text-sm font-semibold tracking-[0.06em] uppercase">
              <span className="text-white">MARKETING. ADS. WEBSITE. AUTOMATION. <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent font-bold">ALL HANDLED</span>. <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent font-bold">ALL IN ONE PLACE</span>.</span>
            </span>
            <Link to="/smartmatch" className="inline-flex items-center gap-2 mx-12 text-xs sm:text-sm font-semibold tracking-[0.06em] uppercase hover:opacity-80 transition-opacity">
              <span className="text-white">YOUR ENTIRE <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-cyan-400 bg-clip-text text-transparent font-bold">MARKETING DEPARTMENT</span>. ONE SUBSCRIPTION. FROM £497 A MONTH</span>
              <ArrowRight size={13} className="text-blue-400" />
            </Link>
            <span className="inline-flex items-center gap-2 mx-12 text-xs sm:text-sm font-semibold tracking-[0.06em] uppercase">
              <span className="text-white">MARKETING. ADS. WEBSITE. AUTOMATION. <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent font-bold">ALL HANDLED</span>. <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent font-bold">ALL IN ONE PLACE</span>.</span>
            </span>
          </div>
        </div>
      </div>
    <nav
      data-testid="navbar"
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "top-0 bg-white shadow-[0_1px_12px_rgba(0,0,0,0.08)]"
          : isLightPage
            ? "top-9 bg-white shadow-[0_1px_8px_rgba(0,0,0,0.04)]"
            : "top-9 bg-transparent"
      }`}
    >
      {/* ══════ DESKTOP (>= 1024px). Single-Row Wide-Screen Layout ══════ */}
      <div className="hidden lg:flex items-center max-w-[1440px] mx-auto px-6 xl:px-10 h-[68px]">
        {/* LEFT. Logo flush to left edge */}
        <div className="flex items-center flex-shrink-0">
          <button
            onClick={openAssistantFromLogo}
            className="flex items-center hover:opacity-90 transition-opacity duration-200"
            data-testid="navbar-logo"
          >
            <StratiqLogo height={42} />
          </button>
        </div>

        {/* CENTER. Navigation tabs evenly spaced */}
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-6 xl:gap-8" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`relative text-[13.5px] font-medium tracking-[0.04em] uppercase transition-colors duration-300 py-1 whitespace-nowrap ${
                location.pathname === link.path
                  ? "text-blue-600"
                  : navDark ? "text-slate-600 hover:text-slate-900" : "text-white/70 hover:text-white"
              }`}
            >
              {link.label}
              {location.pathname === link.path && (
                <motion.div
                  layoutId="nav-underline"
                  className="absolute -bottom-0.5 left-0 right-0 h-[2px] rounded-full bg-blue-600"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
            </Link>
          ))}
          </div>
        </div>

        {/* RIGHT. Phone + Basket + WhatsApp */}
        <div className="flex items-center gap-4 flex-shrink-0" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          <a
            href="tel:08002740058"
            data-testid="nav-phone"
            className={`hidden xl:inline-flex items-center gap-1.5 text-[13px] font-semibold tracking-wide transition-colors duration-300 ${navDark ? "text-slate-500 hover:text-slate-900" : "text-white/70 hover:text-white"}`}
          >
            <Phone size={14} />
            0800 274 0058
          </a>
          <button
            onClick={() => setIsOpen(true)}
            data-testid="nav-basket"
            className={`relative p-2 rounded-lg transition-colors duration-300 ${navDark ? "text-slate-500 hover:text-slate-900 hover:bg-slate-100" : "text-white/70 hover:text-white"}`}
          >
            <ShoppingBag size={18} />
            {itemCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px]">{itemCount}</span>
            )}
          </button>
          <a
            href="https://wa.me/447368945543"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-button"
            className={`rounded-full px-5 py-2 text-[13px] font-semibold tracking-wide transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap inline-flex items-center gap-2 bg-[#25D366] text-white hover:bg-[#1fb855] hover:shadow-green-500/20`}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </a>
        </div>
      </div>

      {/* ══════ MOBILE / TABLET (< 1024px) ══════ */}
      <div className="lg:hidden">
        <div className="flex items-center px-4 md:px-6 h-[60px]">
          {/* Left spacer. matches right actions width for centering on mobile, pushes logo right on tablet */}
          <div className="flex-1 flex md:flex-none md:w-auto md:ml-6">
            <div className="w-[76px] md:w-0" />
          </div>

          {/* Center: Logo. centered on mobile, shifted right on tablet */}
          <button
            onClick={openAssistantFromLogo}
            className="flex items-center hover:opacity-90 transition-opacity mx-auto md:mx-0 md:mr-auto"
            data-testid="navbar-logo-mobile"
            aria-label="Chat with STRATIQ assistant"
          >
            <StratiqLogo height={36} />
          </button>

          {/* Right: Basket + Hamburger */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setIsOpen(true)}
              data-testid="nav-basket-mobile"
              className={`relative p-2 rounded-lg transition-colors duration-300 ${navDark ? "text-slate-600 hover:text-slate-900" : "text-white/80 hover:text-white"}`}
            >
              <ShoppingBag size={20} />
              {itemCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px]">{itemCount}</span>
              )}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`p-2 transition-colors duration-300 ${navDark ? "text-slate-900" : "text-white"}`}
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* ══════ MOBILE MENU DROPDOWN ══════ */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden bg-white border-t border-slate-100 shadow-xl"
          >
            <div className="px-6 py-5 space-y-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`block py-2.5 text-sm font-medium uppercase tracking-wide transition-colors duration-200 ${
                    location.pathname === link.path
                      ? "text-blue-600"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="https://wa.me/447368945543"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-cta-button"
                className="flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full px-6 py-3 text-sm font-semibold mt-4 hover:bg-[#1fb855] transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
              <a
                href="tel:08002740058"
                data-testid="mobile-phone"
                className="flex items-center justify-center gap-2 text-slate-600 font-semibold text-sm mt-3"
              >
                <Phone size={16} />
                0800 274 0058
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
    </>
  );
}
